import { CaretLeft, CaretRight, Check } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { Button } from '~/components/ui/button';
import { cn } from '~/lib/utils';
import { RecommendedChip, splitRecommended } from '~/features/chat/questions-card';
import {
  WIZARD_OTHER,
  clampStep,
  entryState,
  forwardAction,
  goBack,
  goNext,
  goToStep,
  navState,
  progressLabel,
  setOtherText,
  toggleOption,
  toggleSkip,
  type EntryState,
  type WizardQuestion,
  type WizardValue,
} from './answer-wizard-model';

/**
 * The answer wizard (FRONT-3155): a batch of questions answered ONE AT A TIME
 * in a blue band at the top of the reply box. Surface-agnostic — the Builder
 * mounts it today and the chat is meant to reuse it, so it owns no submit
 * button, no network call and nothing about either surface: the host holds
 * `value`, decides what Send means, and passes flags for what the question
 * allows.
 *
 * One forward button, never a separate Skip (eddy, 2026-10-26): on an open
 * question it reads "Skip", once answered "Next" ("Done" on the last), see
 * `forwardAction`. A single question gets no Back and no dots — only "Skip"
 * while it is open. Two or more get a row of progress dots (each one a
 * question, filled once answered, and a button that jumps to it) with Back
 * and the forward button beside them.
 *
 * The band is as tall as the CURRENT question: a short question never sits
 * above the empty space of a long one (eddy, 2026-10-26). Every step stays in
 * the markup, the inactive ones `hidden`, `aria-hidden` and `inert`, so a draft
 * survives moving back and forth and hidden options never reach the Tab order.
 */
export function AnswerWizard({
  questions,
  value,
  onChange,
  allowSkip,
  allowOther,
  idBase,
  disabled = false,
  onDone,
  footer,
}: {
  questions: WizardQuestion[];
  value: WizardValue;
  onChange: (next: WizardValue) => void;
  /** Offer the per-question Skip toggle. */
  allowSkip: boolean;
  /** Offer the free-text "Other" row on every question. */
  allowOther: boolean;
  /** Unique per batch, so option ids never collide between two batches. */
  idBase: number;
  disabled?: boolean;
  /** Done on the last question — the host focuses its Send button, or presses it (the Builder's new project). */
  onDone?: () => void;
  /** A host line at the bottom of the band (the Builder's "why Send is off"). */
  footer?: ReactNode;
}) {
  const count = questions.length;
  const step = clampStep(value.step, count);
  const nav = navState(step, count);
  const several = count > 1;
  const forward = forwardAction(value, allowSkip);
  const skippedHere = !!value.skipped[step];
  // A lone question's row exists only to carry Skip, or to say it was skipped.
  const showNav = several || forward !== 'none' || skippedHere;
  const goForward = () => {
    if (forward === 'skip') onChange(toggleSkip(value, step));
    else if (forward === 'done') onDone?.();
    else if (forward === 'next') onChange(goNext(value));
  };

  return (
    <div
      data-testid="answer-wizard"
      data-question-count={count}
      className="rounded-t-[18px] border-b border-[var(--eddy-blue-line)] bg-[var(--eddy-blue-soft)] px-4 pt-3.5 pb-2.5"
    >
      <div className="grid min-w-0">
        {questions.map((q, qi) => {
          const active = qi === step;
          return (
            <div
              key={qi}
              data-wizard-step={qi}
              data-active={active ? 'true' : 'false'}
              aria-hidden={!active}
              inert={!active}
              className={cn('col-start-1 row-start-1 min-w-0', !active && 'hidden')}
            >
              <p className="px-1 text-[14px] font-medium leading-snug text-foreground text-pretty">{q.question}</p>
              <OptionList
                q={q}
                qi={qi}
                idBase={idBase}
                selected={value.selected[qi] ?? []}
                otherText={value.others[qi] ?? ''}
                dimmed={!!value.skipped[qi]}
                disabled={disabled}
                allowOther={allowOther}
                onToggle={(label) => onChange(toggleOption(value, qi, label, q.multiSelect))}
                onOtherChange={(text) => onChange(setOtherText(value, qi, text))}
              />
            </div>
          );
        })}
      </div>

      {showNav && (
        <div data-testid="answer-wizard-nav" className="mt-2 flex min-h-7 items-center gap-2 px-1">
          {several && (
            <ol aria-label="Questions" className="flex items-center gap-1">
              {questions.map((q, qi) => (
                <li key={qi} className="flex">
                  <StepDot
                    state={entryState(value, qi)}
                    current={qi === step}
                    label={`Question ${qi + 1}: ${q.question}`}
                    disabled={disabled}
                    onClick={() => onChange(goToStep(value, qi))}
                  />
                </li>
              ))}
            </ol>
          )}
          {several && (
            <span
              data-testid="answer-wizard-progress"
              aria-live="polite"
              className="text-[12px] tabular-nums text-muted-foreground"
            >
              {progressLabel(step, count)}
            </span>
          )}
          <div className="ml-auto flex items-center gap-0.5">
            {several && (
              <Button
                type="button"
                size="icon-sm"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:bg-background/70 hover:text-foreground"
                aria-label="Back"
                disabled={disabled || nav.backDisabled}
                tooltip={nav.backReason}
                onClick={() => onChange(goBack(value))}
              >
                <CaretLeft size={13} weight="bold" />
              </Button>
            )}
            {forward === 'none' ? (
              // Only a lone skipped question lands here: say so, quietly.
              <span
                data-testid="answer-wizard-skipped"
                className="flex items-center gap-1 px-2 text-[12px] text-muted-foreground"
              >
                <Check size={12} weight="bold" /> Skipped, EDDY decides
              </span>
            ) : (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                data-testid="answer-wizard-forward"
                data-action={forward}
                className={cn(
                  'h-7 gap-0.5 px-2 text-[12px] font-semibold',
                  // Done finishes the batch: green text, no chevron — there is no "next" after it.
                  forward === 'done'
                    ? 'text-success hover:bg-background/70 hover:text-success'
                    : forward === 'skip'
                      ? 'text-muted-foreground hover:bg-background/70 hover:text-foreground'
                      : 'text-[var(--eddy-blue-text)] hover:bg-background/70 hover:text-[var(--eddy-blue-text)]',
                )}
                disabled={disabled}
                tooltip={forward === 'skip' ? 'Skip it and EDDY decides' : undefined}
                onClick={goForward}
              >
                {forward === 'skip' ? 'Skip' : forward === 'done' ? 'Done' : 'Next'}
                {forward !== 'done' && <CaretRight size={12} weight="bold" />}
              </Button>
            )}
          </div>
        </div>
      )}

      {footer && <div className="mt-1.5 px-1 text-[12px] text-muted-foreground">{footer}</div>}
    </div>
  );
}

/** One progress dot: wide while current, filled once answered, faint once skipped. */
function StepDot({
  state,
  current,
  label,
  disabled,
  onClick,
}: {
  state: EntryState;
  current: boolean;
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-testid="answer-wizard-dot"
      data-state={state}
      aria-current={current ? 'step' : undefined}
      aria-label={`${label} (${state === 'open' ? 'not answered yet' : state})`}
      disabled={disabled}
      onClick={onClick}
      // The hit area is larger than the dot itself.
      className="relative flex h-4 items-center px-[3px] outline-none after:absolute after:-inset-1 focus-visible:rounded-full focus-visible:ring-2 focus-visible:ring-[var(--eddy-blue)]"
    >
      <span
        className={cn(
          'block h-[6px] rounded-full transition-[width,background-color] duration-200 motion-reduce:transition-none',
          current ? 'w-4' : 'w-[6px]',
          state === 'answered'
            ? 'bg-[var(--eddy-blue)]'
            : state === 'skipped'
              ? 'bg-[var(--eddy-blue-line)]'
              : current
                ? 'bg-[var(--eddy-blue)]/60'
                : 'bg-muted-foreground/30',
        )}
      />
    </button>
  );
}

/**
 * The current question's choices as full-width rows — a radio group for one
 * answer, checkboxes for several. A chosen row turns white with a blue edge on
 * the blue band. Roles are real (`radio` / `checkbox` + `aria-checked`) so a
 * screen reader and a role query both see the choice.
 */
function OptionList({
  q,
  qi,
  idBase,
  selected,
  otherText,
  dimmed,
  disabled,
  allowOther,
  onToggle,
  onOtherChange,
}: {
  q: WizardQuestion;
  qi: number;
  idBase: number;
  selected: string[];
  otherText: string;
  dimmed: boolean;
  disabled: boolean;
  allowOther: boolean;
  onToggle: (label: string) => void;
  onOtherChange: (text: string) => void;
}) {
  const role = q.multiSelect ? 'checkbox' : 'radio';
  const hasOther = selected.includes(WIZARD_OTHER);
  return (
    <div
      role={q.multiSelect ? 'group' : 'radiogroup'}
      aria-label={q.question}
      className={cn('mt-1.5 max-h-[220px] space-y-0.5 overflow-y-auto', dimmed && 'opacity-55')}
    >
      {q.options.map((opt, oi) => {
        const checked = selected.includes(opt.label);
        const label = splitRecommended(opt.label);
        return (
          <OptionRow
            key={opt.label}
            id={`aw-${idBase}-${qi}-${oi}`}
            role={role}
            checked={checked}
            disabled={disabled}
            onClick={() => onToggle(opt.label)}
          >
            <span className="font-medium">{label.text}</span>
            {label.recommended && <RecommendedChip />}
            {opt.description && <span className="block text-[12px] text-muted-foreground">{opt.description}</span>}
          </OptionRow>
        );
      })}
      {allowOther && (
        <>
          <OptionRow
            id={`aw-${idBase}-${qi}-other`}
            role={role}
            checked={hasOther}
            disabled={disabled}
            onClick={() => onToggle(WIZARD_OTHER)}
          >
            <span className="font-medium">Other</span>
          </OptionRow>
          {hasOther && (
            <input
              type="text"
              value={otherText}
              disabled={disabled}
              onChange={(e) => onOtherChange(e.target.value)}
              placeholder="Type your answer"
              aria-label="Your own answer"
              className="ml-[34px] w-[calc(100%-34px)] rounded-md border border-[var(--eddy-blue-line)] bg-background px-2 py-1 text-[13px] outline-none focus-visible:border-[var(--eddy-blue)] focus-visible:ring-2 focus-visible:ring-[var(--eddy-blue-soft)]"
            />
          )}
        </>
      )}
    </div>
  );
}

function OptionRow({
  id,
  role,
  checked,
  disabled,
  onClick,
  children,
}: {
  id: string;
  role: 'radio' | 'checkbox';
  checked: boolean;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      id={id}
      role={role}
      aria-checked={checked}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-[9px] px-2 py-[7px] text-left text-[13px] leading-snug text-foreground outline-none transition-colors',
        'hover:bg-background/70 focus-visible:ring-2 focus-visible:ring-[var(--eddy-blue)] disabled:cursor-not-allowed disabled:opacity-60',
        checked && 'bg-background shadow-[inset_0_0_0_1.5px_var(--eddy-blue)] hover:bg-background',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'flex size-4 shrink-0 items-center justify-center border-[1.5px] bg-background transition-colors',
          role === 'radio' ? 'rounded-full' : 'rounded-[4px]',
          checked ? 'border-[var(--eddy-blue)]' : 'border-muted-foreground/40',
          checked && role === 'checkbox' && 'bg-[var(--eddy-blue)]',
        )}
      >
        {checked &&
          (role === 'radio' ? (
            <span className="size-2 rounded-full bg-[var(--eddy-blue)]" />
          ) : (
            <Check size={10} weight="bold" className="text-white" />
          ))}
      </span>
      <span className="min-w-0 flex-1">{children}</span>
    </button>
  );
}
